import {
  GenericTable,
  GenericTableVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import { clone, cloneDeep, get, noop, set } from "lodash";
import { array, bool, func, number, object, string } from "prop-types";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { DROP_POSITIONS } from "./constants";
import classes from "./ExcelTable.module.scss";
import ExcelTableCell from "./ExcelTableCell";
import ExcelTableContext from "./ExcelTableContext";
import HeaderCell from "./HeaderCell";
import { findRowIndexById, getPathToTableRow } from "./utils";
import moveItemBySourceId from "./utils/moveItemBySourceId";

const HOVER_PREFIX = "table__row--hover";
const SCROLL_TO = {
  align: "center",
  behavior: "auto",
};

const getClosestTableRow = (e) => {
  return e.target.closest(`.${CSS.escape(classes.table__row)}`);
};

const resetDragOverClasses = (rowEl) => {
  if (rowEl) {
    rowEl.classList.remove(classes[`${HOVER_PREFIX}`]);
    Object.values(DROP_POSITIONS).forEach((placement) => {
      rowEl.classList.remove(classes[`${HOVER_PREFIX}-${placement}`]);
    });
  }
};

const ExcelTable = forwardRef((params, ref) => {
  const {
    cellValidationErrors = {},
    className,
    commitEditOnChange = false,
    defaultSortColumnIndex,
    disabledMessage,
    doubleClickToEdit = false,
    draggableColumnIndex = 0,
    dragTopThreshold = 0.5,
    dragBottomThreshold = 0.5,
    enableClickableDisabledRows = false,
    expandableColumnIndex = 0,
    expandAllSubRows,
    getExpandedRowModel,
    getIsRowExpanded,
    getRowCanExpand,
    indentAtColumnIndex = 0,
    isDraggable = false,
    isRowDragDisabled = () => false,
    validateEditableCellClick = () => true,
    validateDragOver = () => true,
    validateDrop = () => true,
    isCellDisabled,
    isRowDisabled,
    isEditing = false,
    isVirtualized = false,
    onCellChangeFinished = noop,
    onDragFinished = noop,
    onRowClick = noop,
    onRowMouseEnter = noop,
    onRowMouseLeave = noop,
    overscan = 20,
    rowIdKey = "id",
    sanitizeUserInput,
    selectedIndex,
    subRowKey,
    emptyMessage,
    ...props
  } = params;
  const [data, setData] = useState(props.data);
  const [draggedRow, setDraggedRow] = useState(null);
  const draggedRowRef = useRef(null);
  const [currentCellId, setCurrentCellId] = useState(null);
  const tableRef = useRef(null);
  const timeoutRef = useRef(null);
  const flatRowIndexMap = tableRef.current?.flatRowIndexMap;

  const setDraggedRowWithRef = useCallback((row) => {
    draggedRowRef.current = row;
    setDraggedRow(row);
  }, []);

  const finalColumns = useMemo(() => {
    const columns = clone(props.columns || []);

    return columns.map(({ cell, header, ...col }, index) => {
      const isExpandableCol = expandableColumnIndex === index;
      return {
        ...col,
        header: ({ table }) => (
          <HeaderCell
            editKey={col.editKey}
            header={header}
            colIndex={index}
            table={table}
          />
        ),
        enableSorting: isExpandableCol ? false : col.enableSorting,
        originalCell: cell,
        cell: ({ getValue, row, ...restOfRowData }) => (
          <ExcelTableCell
            cell={cell}
            colIndex={index}
            getValue={getValue}
            isCellDisabled={isCellDisabled}
            row={row}
            {...restOfRowData}
          />
        ),
      };
    });
  }, [props.columns]);

  const getCellDepth = useCallback(
    (row) => {
      const override = props.getCellDepth;
      if (override) {
        return override(row);
      }

      return row.depth || 0;
    },
    [props.getCellDepth]
  );

  const getMouseYPosition = useCallback((e, element) => {
    const rect = element.getBoundingClientRect();
    const height = rect.bottom - rect.top;
    const mouseY = e.clientY - rect.top;
    const yRatio = mouseY / height;

    switch (true) {
      case yRatio < dragTopThreshold:
        return DROP_POSITIONS.TOP;
      case yRatio >= dragBottomThreshold:
        return DROP_POSITIONS.BOTTOM;
      default:
        return DROP_POSITIONS.MIDDLE;
    }
  }, []);

  const areRowsEqual = (sourceRow, targetRow) => {
    return sourceRow.id === targetRow.id;
  };

  const onSubmitCellEdit = ({ valueKey, row, value } = {}) => {
    const pathToEdit = getPathToTableRow(row, { subRowKey });

    setCurrentCellId(null);

    setData((state) => {
      const pathToValue = [...pathToEdit, valueKey];
      const mutableState = cloneDeep(state);
      // We gotta clone the state, 'cos React freezes any object
      // data and makes it immutable in this scope.

      const updated = set(mutableState, pathToValue, value);
      onCellChangeFinished(updated, { pathToValue, valueKey, row, value });

      return updated;
    });
  };

  const onRowDragOver = (e, toRow) => {
    e.preventDefault();

    const rowEl = getClosestTableRow(e);
    if (!rowEl || !draggedRowRef.current) {
      return;
    }

    const currentDraggedRow = draggedRowRef.current;
    const placement = getMouseYPosition(e, rowEl);

    resetDragOverClasses(rowEl);

    if (
      areRowsEqual(currentDraggedRow, toRow) ||
      !validateDragOver(currentDraggedRow, toRow, placement)
    ) {
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "none";
      }

      rowEl.classList.add(classes[`${HOVER_PREFIX}`]);
      rowEl.classList.add(
        classes[`${HOVER_PREFIX}-${DROP_POSITIONS.FORBIDDEN}`]
      );
      return;
    }

    rowEl.classList.add(classes[`${HOVER_PREFIX}`]);
    rowEl.classList.add(classes[`${HOVER_PREFIX}-${placement}`]);
  };

  const onRowDragLeave = (e) => {
    const rowEl = getClosestTableRow(e);
    resetDragOverClasses(rowEl);
  };

  const onRowDrop = (e, toRow) => {
    e.preventDefault();

    const toRowEl = getClosestTableRow(e);
    if (!toRowEl || !draggedRowRef.current) {
      return;
    }

    resetDragOverClasses(toRowEl);

    const currentDraggedRow = draggedRowRef.current;
    const placement = getMouseYPosition(e, toRowEl);

    if (
      areRowsEqual(currentDraggedRow, toRow) ||
      !validateDrop(currentDraggedRow, toRow, placement)
    ) {
      return;
    }

    const moveResult = moveItemBySourceId(data, {
      sourceId: get(currentDraggedRow.original, rowIdKey),
      targetId: get(toRow.original, rowIdKey),
      childrenKey: subRowKey,
      placement,
    });

    setData(moveResult.success ? moveResult.items : data);

    onDragFinished(moveResult.items);
  };

  const focusByCellId = async (id, colName) => {
    if (!tableRef?.current || !id) {
      return;
    }

    return new Promise((resolve) => {
      timeoutRef.current = setTimeout(() => {
        const { flatRowIndexMap, table } = tableRef.current;
        const rowId = String(id);
        const index = findRowIndexById(table, rowId);
        const renderIndex = flatRowIndexMap.get(rowId);
        const cellId = `${rowId}_${colName}`;

        if (index > -1) {
          tableRef.current.scrollToRow(index, SCROLL_TO);
        }

        setCurrentCellId(cellId);
        resolve(renderIndex);
      }, 200);
    });
  };

  const isRowVisible = (id) =>
    Boolean(
      tableRef.current?.table
        ?.getRowModel()
        ?.rows.some((row) => row.id === String(id))
    );

  const getRowByIndex = (index) => {
    const { table } = tableRef.current;
    if (!table?.getRowModel) return null;

    const rowModel = table.getRowModel();
    return get(rowModel, ["flatRows", index]);
  };

  useEffect(() => {
    setData(props.data || []);
  }, [props.data]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useImperativeHandle(ref, () => {
    return {
      getIndexMap: () => tableRef.current?.flatRowIndexMap,
      getRowByIndex,
      flatRowIndexMap,
      focusByCellId,
      isRowVisible,
      ...tableRef.current,
    };
  }, [flatRowIndexMap]);

  const contextValue = useMemo(
    () => ({
      cellValidationErrors,
      commitEditOnChange,
      currentCellId,
      doubleClickToEdit,
      draggedRow,
      draggableColumnIndex,
      expandableColumnIndex,
      getCellDepth,
      indentAtColumnIndex,
      isDraggable,
      isEditing,
      isRowDragDisabled,
      onSubmitCellEdit,
      rowIdKey,
      sanitizeUserInput,
      setCurrentCellId,
      setDraggedRow: setDraggedRowWithRef,
      subRowKey,
      validateEditableCellClick,
      emptyMessage,
    }),
    [
      cellValidationErrors,
      commitEditOnChange,
      currentCellId,
      doubleClickToEdit,
      draggedRow,
      draggableColumnIndex,
      expandableColumnIndex,
      getCellDepth,
      indentAtColumnIndex,
      isDraggable,
      isEditing,
      isRowDragDisabled,
      onSubmitCellEdit,
      rowIdKey,
      sanitizeUserInput,
      setCurrentCellId,
      setDraggedRowWithRef,
      subRowKey,
      validateEditableCellClick,
      emptyMessage,
    ]
  );

  return (
    <ExcelTableContext.Provider value={contextValue}>
      <GenericTable
        activeClassName={classes["table__row--active"]}
        cellClassName={clsx(classes["table_cell"], props.cellClassName)}
        className={clsx(classes["table"], className)}
        columns={finalColumns}
        data={data}
        defaultSortColumnIndex={defaultSortColumnIndex}
        disabledMessage={disabledMessage}
        enableClickableDisabledRows={enableClickableDisabledRows}
        expandAllSubRows={expandAllSubRows}
        getExpandedRowModel={getExpandedRowModel}
        getIsRowExpanded={getIsRowExpanded}
        getRowCanExpand={getRowCanExpand}
        headerCellClassName={props.headerCellClassName}
        isVirtualized={isVirtualized}
        isRowDisabled={isRowDisabled}
        onRowClick={isEditing ? onRowClick : undefined}
        onRowDragOver={onRowDragOver}
        onRowDragLeave={onRowDragLeave}
        onRowDrop={onRowDrop}
        onRowMouseEnter={onRowMouseEnter}
        onRowMouseLeave={onRowMouseLeave}
        overscan={overscan}
        rowClassName={clsx(classes.table__row, props.rowClassName)}
        selectedIndex={selectedIndex}
        subRowKey={subRowKey}
        ref={tableRef}
        rowIdKey={rowIdKey}
        emptyMessage={emptyMessage}
        variant={GenericTableVariant.bordered}
      />
    </ExcelTableContext.Provider>
  );
});

ExcelTable.displayName = "ExcelTable";

ExcelTable.propTypes = {
  cellValidationErrors: object,
  columns: array,
  cellClassName: string,
  className: string,
  commitEditOnChange: bool,
  data: array,
  defaultSortColumnIndex: number,
  disabledMessage: string,
  doubleClickToEdit: bool,
  draggableColumnIndex: number,
  enableClickableDisabledRows: bool,
  expandableColumnIndex: number,
  expandAllSubRows: bool,
  getCellDepth: func,
  getExpandedRowModel: func,
  getIsRowExpanded: func,
  getRowCanExpand: func,
  headerCellClassName: string,
  indentAtColumnIndex: number,
  isDraggable: bool,
  isEditing: bool,
  isRowDragDisabled: func,
  isVirtualized: bool,
  onCellChangeFinished: func,
  onDragFinished: func,
  onRowClick: func,
  onRowMouseEnter: func,
  onRowMouseLeave: func,
  overscan: number,
  rowClassName: string,
  rowIdKey: string,
  sanitizeUserInput: func,
  selectedIndex: number,
  subRowKey: string,
  validateDrop: func,
  emptyMessage: string,
};

export default ExcelTable;
